import { observer } from 'mobx-react-lite';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Button, Dimmer, Loader, Table } from 'semantic-ui-react';
import agent from '../../../../../app/api/agent';
import { useStore } from '../../../../../app/stores/store';
import { toast } from 'react-toastify';
import {
  INITIAL_TASK_LIST,
  NewTimeSheetEntry,
  NFAIProductivityStatusCreate,
  NFAITimeSheetMasteringProps,
  Project,
  QCMarker,
  ScopeItem,
  statusNewOptions,
  TaskListState,
  TaskOption,
  TimeSheetNewHeader,
} from './types';
import { useCurrentDateTime, useFormData } from './hooks/useFormData';
import { ProjectSelector } from './ProjectSelector';
import TableColumnsComponent from './TableColumnsComponent';
import RenderActionButtons from './RenderActionButtons';
import { calculateDuration } from '../../../../../utils/calculateDuration';
import { v4 as uuid } from 'uuid';
import { formatLocalTimeToISOString } from '../TimeSheet';
import axios from 'axios';

const createHandlers = (updateFormField: Function) => ({
  handleNumberInput: (field: string, value: string) => {
    const parsedValue = value === '' ? 0 : parseInt(value, 10);
    updateFormField(field, isNaN(parsedValue) ? 0 : parsedValue);
  },
});

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    width: '300px',
    padding: '16px',
    border: '1px solid #ccc',
    borderRadius: '6px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    backgroundColor: '#fafafa',
  },
  input: {
    padding: '6px',
  },
  fileName: {
    fontSize: '14px',
    color: '#333',
  },
  button: {
    padding: '8px',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
  },
};

const NFAITimeSheetMastering: React.FC<NFAITimeSheetMasteringProps> = ({
  titleLoader,
  projectList,
  isUserAuthorized,
}) => {
  const {
    userStore: { user },
  } = useStore();

  const { formData, updateFormField, resetForm, populateFormFromEntry } =
    useFormData();
  const { getCurrentDateTime } = useCurrentDateTime();

  const [selectedProject, setSelectedProject] = useState<string>('');
  const [rows, setRows] = useState<NewTimeSheetEntry[]>([]);
  const [titleList, setTitleList] = useState<Project[]>([]);
  const [taskList, setTaskList] = useState<TaskListState>(INITIAL_TASK_LIST);

  // Loading state
  const [loader, setLoader] = useState<boolean>(false);

  // File upload
  const [file, setFile] = useState<File | null>(null);

  const [tableLoader, setTableLoader] = useState<boolean>(false);

  // Derived values
  const department = user?.departmentName || '';
  const empId = user?.displayName || '';

  // ============================================
  // CRITICAL: Current user's active entry check
  // This filters by emp_id so each user has independent UI state
  // ============================================
  const currentUserActiveEntry = useMemo(() => {
    return rows.find(
      (r) =>
        (r.actions === '1' || r.actions === '4') &&
        r.emp_id === user?.displayName
    );
  }, [rows, user?.displayName]);

  // Memoized computed values
  const computedValues = useMemo(
    () => ({
      isProjectSelected: selectedProject !== '',
      isScopeSelected: formData.scope_of_Work !== '',
      isStatusSelected: formData.status !== '',
      isStartReel: formData.start_Reel !== 0,
      isEndReel: formData.end_Reel !== 0,
    }),
    [
      selectedProject,
      formData.scope_of_Work,
      formData.status,
      formData.start_Reel,
      formData.end_Reel,
    ]
  );

  // Calculate total Reel
  useEffect(() => {
    const { start_Reel, end_Reel } = formData;
    if (
      start_Reel !== 0 &&
      end_Reel !== 0 &&
      !isNaN(start_Reel) &&
      !isNaN(end_Reel)
    ) {
      const reelDiff = end_Reel - start_Reel + 1;
      updateFormField('comp_Reel', reelDiff);
    } else {
      updateFormField('comp_Reel', 0);
    }
     //eslint-disable-next-line
  }, [formData.start_Reel, formData.end_Reel, updateFormField]);

  // Calculate duration
  useEffect(() => {
    const duration = calculateDuration(formData.start_Time, formData.end_Time);
    updateFormField('duration', duration);
  }, [formData.start_Time, formData.end_Time, updateFormField]);

  // Fetch scope list
  useEffect(() => {
    if (!department) return;

    const fetchScopeList = async () => {
      try {
        const { data: list } =
          await agent.NFAIRestoTimeSheet.getScopeOfWorkList(department);

        const uniqueTaskOptions: TaskOption[] = list.reduce(
          (acc: TaskOption[], item: ScopeItem) => {
            if (!acc.some((opt) => opt.value === item.task)) {
              acc.push({
                key: item.id,
                value: item.task,
                text: item.task,
              });
            }
            return acc;
          },
          []
        );

        // Filter tasks that have available scopes
        const availableTaskOptions = uniqueTaskOptions.filter((taskOption) => {
          const allScopesForTask = list
            .filter((item: ScopeItem) => item.task === taskOption.value)
            .map((item: ScopeItem) => item.name);

          const usedScopes = rows
            .filter((row) => row.task === taskOption.value)
            .map((row) => row.scope_of_Work);

          const availableScopes = allScopesForTask.filter(
            (scope: any) => !usedScopes.includes(scope)
          );

          return availableScopes.length > 0;
        });

        setTaskList({
          selected: '',
          subtaskList: list,
          options: availableTaskOptions,
        });
      } catch (error) {
        console.error('Error fetching scope list:', error);
      }
    };

    fetchScopeList();
  }, [department, rows]);

  // Fetch project list
  useEffect(() => {
    if (!user?.id) return;

    const fetchTitleList = async () => {
      setLoader(true);
      try {
        const { data: list } =
          await agent.NFAIRestoTimeSheet.NFAITimeSheetProjectForMastering(
            user.id
          );

        const loadTitleList = list.map((item: Project) => ({
          id: item.id,
          name: item.name,
          value: item.name,
        }));

        setTitleList(loadTitleList);
      } catch (error) {
        console.error('Error fetching project list:', error);
      } finally {
        setLoader(false);
      }
    };

    fetchTitleList();
  }, [user?.id]);

  // Fetch entries when project changes
  useEffect(() => {
    if (!selectedProject || !department) return;

    const fetchEntriesList = async () => {
      try {
        const { data: list } =
          await agent.NFAIRestoTimeSheet.getTimeSheetEntries(
            selectedProject,
            department
          );
        setRows(list);
      } catch (error) {
        console.error('Error fetching entries list:', error);
      }
    };

    fetchEntriesList();
  }, [selectedProject, department]);

  // ============================================
  // FIXED: Only populate form from CURRENT USER's active row
  // This was the bug - it was populating from ANY active row
  // ============================================
useEffect(() => {
  const activeRow = rows.find(
    (x) =>
       //eslint-disable-next-line
      x.actions === ActionState.STARTED ||
       //eslint-disable-next-line
      x.actions === ActionState.RESUME,
  );

  if (activeRow && activeRow.id !== formData.id) {
    populateFormFromEntry(activeRow);
  }
   //eslint-disable-next-line
}, [rows, formData.id, populateFormFromEntry]);


  // Event handlers
  const handleStartTimeChange = useCallback(
    (date: Date | null) => {
      if (date) {
        updateFormField('start_Time', date);
        updateFormField('end_Time', new Date(date.getTime() + 60000));
      }
    },
    [updateFormField]
  );

  const handleTaskSelection = useCallback(
    (value: string) => {
      setTaskList((prev) => ({ ...prev, selected: value }));
      updateFormField('task', value);
      updateFormField('scope_of_Work', '');
    },
    [updateFormField]
  );

  const getScopeOptions = useCallback(
    (selectedTask?: string, currentRowId?: string) => {
      if (!selectedTask) return [];

      const allScopes =
        taskList.subtaskList?.filter(
          (item: ScopeItem) => item.task === selectedTask
        ) ?? [];
        //eslint-disable-next-line
      // Exclude current row + completed/held from "used"
      // const usedScopes = rows
      //   .filter(
      //     (row) => row.task === selectedTask && row.id !== currentRowId, // Don't exclude current editing row
      //     // && !['2', '3'].includes(row.actions || '') // Don't block completed/held scopes
      //   )
      //   .map((row) => row.scope_of_Work)
      //   .filter(Boolean); // Remove empty scopes

      return allScopes.map((scope: ScopeItem) => ({
        key: scope.id,
        value: scope.value,
        text: scope.value,
      }));
    },
    // eslint-disable-next-line
    [taskList.subtaskList, rows],
  );

  // Render helpers
  const projectOptions = useMemo(() => {
    const projects = isUserAuthorized ? projectList : titleList;
    return projects.map((project) => ({
      key: project.id,
      value: project.id,
      text: project.name,
    }));
  }, [isUserAuthorized, projectList, titleList]);

  const handleActionChange = async (
    showRow: NewTimeSheetEntry,
    action: number
  ) => {
    try {
      const newId = uuid();
      let dataToSend: any;

      switch (action) {
        case 1: // Start
          const startData = {
            ...formData,
            id: newId,
            emp_id: user?.displayName || '',
            action_Name: user?.displayName || '',
            projectID: selectedProject || '',
            project_Name:
              projectList.find((p) => p.id === selectedProject)?.name || '',
            department: user?.departmentName || '',
            jumbo_Reel: formData.jumbo_Reel || '',
            start_Time: getCurrentDateTime(),
            end_Time: getCurrentDateTime(),
            duration: '00:00',
            actions: '1',
          };
          populateFormFromEntry(startData);
          const updateStartDate = {
            ...startData,
            start_Time: formatLocalTimeToISOString(startData.start_Time),
            end_Time: formatLocalTimeToISOString(startData.end_Time),
          };
          dataToSend = updateStartDate;
          break;

        case 2: // Complete
          const endTime = getCurrentDateTime();
          const duration = calculateDuration(showRow.start_Time, endTime);

          const completeData = {
            ...showRow,
            jumbo_Reel: showRow.jumbo_Reel || '',
            end_Time: endTime,
            duration: duration,
            actions: '2',
          };
          setRows((prevRows) =>
            prevRows.map((row) => (row.id === showRow.id ? completeData : row))
          );
          const updateCompleteData = {
            ...completeData,
            start_Time: formatLocalTimeToISOString(completeData.start_Time),
            end_Time: formatLocalTimeToISOString(completeData.end_Time),
          };
          dataToSend = updateCompleteData;
          break;

        case 3: // Hold
          const holdData = {
            ...showRow,
            jumbo_Reel: showRow.jumbo_Reel || '',
            actions: '3',
          };
          setRows((prevRows) =>
            prevRows.map((row) => (row.id === showRow.id ? holdData : row))
          );
          const updateHoldData = {
            ...holdData,
            start_Time: formatLocalTimeToISOString(holdData.start_Time),
            end_Time: formatLocalTimeToISOString(holdData.end_Time),
          };
          dataToSend = updateHoldData;
          break;

        case 4: // Resume
          const resumeData = {
            ...showRow,
            jumbo_Reel: showRow.jumbo_Reel || '',
            actions: '4',
          };
          const updateResumeData = {
            ...resumeData,
            start_Time: formatLocalTimeToISOString(resumeData.start_Time),
            end_Time: formatLocalTimeToISOString(resumeData.end_Time),
          };
          dataToSend = updateResumeData;
          break;

        default:
          throw new Error('Invalid action');
      }

      const { data } = await agent.viewPage.getParentMetaData({
        itemId: dataToSend.projectID,
      });

      const uccId =
        data.find((el) => el.elementName === 'UCCID')?.elementValue || '';
      const batch =
        data.find((el) => el.elementName === 'Batch #')?.elementValue || '';
      const totalReels =
        data.find((el) => el.elementName === 'Total Reels')?.elementValue || '';
      const typeOfFilm =
        data.find((el) => el.elementName === 'Film Type')?.elementValue || '';

      let nfaiProductivitySend: NFAIProductivityStatusCreate = {
        Id: dataToSend?.id || '',
        ProjectId: dataToSend.projectID || '',
        Date: new Date(),
        Emp_ID: dataToSend.emp_id,
        Emp_Name: user?.username || '',
        Title: dataToSend?.project_Name || '',
        UCC_ID: uccId,
        BATCH: batch,
        Total_Reel: totalReels,
        jumbo_Reel: dataToSend.jumbo_Reel || '',
        Department: user?.departmentName || '',
        Total_Scanned_Frames: '',
        Total_Duration: dataToSend?.duration || '',
        no_of_Corrections: dataToSend?.no_of_Corrections || '',
        types_of_Correction: dataToSend?.types_of_Correction || '',
        Type_of_Film: typeOfFilm,
        Task: dataToSend?.task || '',
        Scope_of_work: dataToSend?.scope_of_Work || '',
        Start_Reel: dataToSend?.start_Reel || '',
        End_Reel: dataToSend?.end_Reel || '',
        Comp_Reel: dataToSend?.comp_Reel || '',
        Comp_Reel_Frames: '',
        Comp_Reel_Frames_Duration: '',
        Start_time: dataToSend?.start_Time || '',
        End_time: dataToSend?.end_Time || '',
        Total_Hours: '',
        DPX_Source_In: dataToSend?.dpX_Source_In || '',
        DPX_Source_Out: dataToSend?.dpX_Source_Out || '',
        Error_Duration: dataToSend?.error_Duration || '',
        MOV_Record_In: dataToSend?.moV_Record_In || '',
        MOV_Record_Out: dataToSend?.moV_Record_Out || '',
        Error_Code: dataToSend?.error_Code || '',
        Error_Remarks: dataToSend?.error_Remarks || '',
        Status: dataToSend?.actions || '',
        Remarks: dataToSend?.remarks || '',
        ProductionHours: 0,
      };

      await agent.NFAIRestoTimeSheet.NFAIProductivityStatusCreate(
        nfaiProductivitySend
      );

      await agent.NFAIRestoTimeSheet.NFAIMasteringProjectTimeSheetEntry(
        dataToSend
      );
      toast.success('Timesheet entry updated successfully');

      const { data: list } = await agent.NFAIRestoTimeSheet.getTimeSheetEntries(
        selectedProject || '',
        user?.departmentName || ''
      );
      setRows(list);

      if (action === 2 || action === 3) {
        resetForm();
        setTaskList((prev) => ({ ...prev, selected: '' }));
      }
    } catch (error) {
      console.error('Error updating timesheet:', error);
      toast.error('Failed to update timesheet entry');
    }
  };
  // 2. Create action state enum
  enum ActionState {
    INITIAL = '',
    STARTED = '1',
    COMPLETED = '2',
    HOLD = '3',
    RESUME = '4',
  }

  // 3. Fix useEffect dependencies
  useEffect(() => {
    const activeRow = rows.find(
      (x) =>
        x.actions === ActionState.STARTED || x.actions === ActionState.RESUME,
    );
    if (activeRow && activeRow.id !== formData.id) {
      populateFormFromEntry(activeRow);
    }
    // eslint-disable-next-line
  }, [rows, formData.id, populateFormFromEntry]);

  // ============================================
  // FIXED: renderTableRow - passes user-specific props
  // ============================================
  const renderTableRow = (row: NewTimeSheetEntry) => {
    const isEditingThisRow = row.actions === '1' || row.actions === '4';
    const isCurrentUserRow = row.emp_id === user?.displayName;

    const handlers = {
      updateFormField,
      handleStartTimeChange,
      handleTaskSelection,
      getScopeOptions,
      computedValues,
      ...createHandlers(updateFormField),
    };

    // Only render editable form if this is the CURRENT USER's active row
    if (isEditingThisRow && isCurrentUserRow) {
      const options = {
        empId,
        taskList,
        statusOptions: statusNewOptions,
      };
      return (
        <>
          <TableColumnsComponent
            options={options}
            formData={formData}
            handlers={{
              ...handlers,
              getScopeOptions: (task: string) =>
                getScopeOptions(task, formData.id),
            }}
          />
          <Table.Cell>
            <RenderActionButtons
              showRow={formData}
              currentUserHasActiveEntry={!!currentUserActiveEntry}
              currentUserActiveEntryId={currentUserActiveEntry?.id}
              handleActionChange={handleActionChange}
              isOwnEntry={true}
            />
          </Table.Cell>
        </>
      );
    }

    // For OTHER USER's active rows or any non-active rows, show read-only data
    return (
      <>
        <Table.Cell>{row.emp_id}</Table.Cell>
        <Table.Cell>{row.jumbo_Reel}</Table.Cell>
        <Table.Cell>{row.start_Reel}</Table.Cell>
        <Table.Cell>{row.end_Reel}</Table.Cell>
        <Table.Cell>{row.comp_Reel}</Table.Cell>
        <Table.Cell>
          {row.start_Time
            ? new Date(row.start_Time).toLocaleString('en-IN', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
              })
            : '-'}
        </Table.Cell>
        <Table.Cell>
          {row.end_Time
            ? new Date(row.end_Time).toLocaleString('en-IN', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
              })
            : '-'}
        </Table.Cell>
        <Table.Cell>{row.duration}</Table.Cell>
        <Table.Cell>{row.task}</Table.Cell>
        <Table.Cell>{row.scope_of_Work}</Table.Cell>
        <Table.Cell>{row.dpX_Source_In || ''}</Table.Cell>
        <Table.Cell>{row.dpX_Source_Out || ''}</Table.Cell>
        <Table.Cell>{row.error_Duration || ''}</Table.Cell>
        <Table.Cell>{row.moV_Record_In || ''}</Table.Cell>
        <Table.Cell>{row.moV_Record_Out || ''}</Table.Cell>
        <Table.Cell>{row.no_of_Corrections}</Table.Cell>
        <Table.Cell>{row.types_of_Correction}</Table.Cell>
        <Table.Cell>{row.error_Code || ''}</Table.Cell>
        <Table.Cell>{row.error_Remarks || ''}</Table.Cell>
        <Table.Cell>{row.status}</Table.Cell>
        <Table.Cell>{row.remarks}</Table.Cell>
        <Table.Cell>
          <RenderActionButtons
            showRow={row}
            currentUserHasActiveEntry={!!currentUserActiveEntry}
            currentUserActiveEntryId={currentUserActiveEntry?.id}
            handleActionChange={handleActionChange}
            isOwnEntry={isCurrentUserRow}
          />
        </Table.Cell>
      </>
    );
  };

  const renderNewEntryRow = () => {
    const options = {
      empId,
      taskList,
      statusOptions: statusNewOptions,
    };

    const handlers = {
      updateFormField,
      handleStartTimeChange,
      handleTaskSelection,
      getScopeOptions,
      computedValues,
      ...createHandlers(updateFormField),
    };

    const updateData = {
      ...formData,
      actions: '',
    };

    return (
      <Table.Row>
        <TableColumnsComponent
          options={options}
          formData={updateData}
          handlers={{
            ...handlers,
            getScopeOptions: (task: string) => getScopeOptions(task, undefined),
          }}
        />
        <Table.Cell>
          <RenderActionButtons
            showRow={updateData}
            currentUserHasActiveEntry={!!currentUserActiveEntry}
            currentUserActiveEntryId={currentUserActiveEntry?.id}
            handleActionChange={handleActionChange}
            isOwnEntry={true}
          />
        </Table.Cell>
      </Table.Row>
    );
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) {
      setFile(null);
      return;
    }
    setFile(e.target.files[0]);
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('No file selected');
      return;
    }

    try {
      setTableLoader(true);
      const formData1 = new FormData();
      formData1.append('file', file);

      const response = await axios.post<QCMarker[]>(
        `${axios.defaults.baseURL}/NFAIRestorationTimeSheet/QCErrorFileCSV`,
        formData1,
        { headers: { 'Content-type': 'multipart/form-data' } }
      );

      const markers = response.data;

      if (!markers.length) {
        toast.error('No markers found in file');
        return;
      }

      const { data } = await agent.viewPage.getParentMetaData({
        itemId: selectedProject || '',
      });

      const uccId =
        data.find((el) => el.elementName === 'UCCID')?.elementValue || '';
      const batch =
        data.find((el) => el.elementName === 'Batch #')?.elementValue || '';
      const totalReels =
        data.find((el) => el.elementName === 'Total Reels')?.elementValue || '';
      const typeOfFilm =
        data.find((el) => el.elementName === 'Film Type')?.elementValue || '';

      for (const marker of markers) {
        const entry: NewTimeSheetEntry = {
          id: uuid(),
          emp_id: user?.displayName || '',
          projectID: selectedProject || '',
          project_Name:
            projectList.find((p) => p.id === selectedProject)?.name || '',
          department: user?.departmentName || '',
          jumbo_Reel: formData.jumbo_Reel || '',
          start_Reel: formData.start_Reel || 0,
          end_Reel: formData.end_Reel || 0,
          comp_Reel: formData.comp_Reel || 0,
          task: formData.task || '',
          scope_of_Work: formData.scope_of_Work || '',
          error_Duration: marker.recordDuration || '',
          start_Time: getCurrentDateTime(),
          end_Time: getCurrentDateTime(),
          duration: '00:00',
          actions: '2',
          dpX_Source_In: marker.sourceIn,
          dpX_Source_Out: marker.sourceOut,
          moV_Record_In: marker.recordIn,
          moV_Record_Out: marker.recordOut,
          error_Remarks: marker.notes,
          error_Code: marker.color,
          no_of_Corrections: Number(formData.no_of_Corrections) || 0,
          types_of_Correction: formData.types_of_Correction || '',
          remarks: formData.remarks || '',
          action_Name: user?.displayName || '',
          status: formData.status || '',
        };

        const nfaiProductivitySend: NFAIProductivityStatusCreate = {
          Id: entry.id,
          ProjectId: entry.projectID,
          Date: new Date(),
          Emp_ID: entry.emp_id,
          Emp_Name: user?.username || '',
          Title: entry.project_Name,
          UCC_ID: uccId,
          BATCH: batch,
          Total_Reel: totalReels,
          jumbo_Reel: entry.jumbo_Reel,
          Department: user?.departmentName || '',
          Total_Scanned_Frames: '',
          Total_Duration: entry.duration,
          no_of_Corrections: String(entry.no_of_Corrections),
          types_of_Correction: entry.types_of_Correction,
          Type_of_Film: typeOfFilm,
          Task: entry.task,
          Scope_of_work: entry.scope_of_Work,
          Start_Reel: String(entry.start_Reel),
          End_Reel: String(entry.end_Reel),
          Comp_Reel: String(entry.comp_Reel),
          Comp_Reel_Frames: '',
          Comp_Reel_Frames_Duration: '',
          Start_time: entry.start_Time,
          End_time: entry.end_Time,
          Total_Hours: '',
          DPX_Source_In: entry.dpX_Source_In,
          DPX_Source_Out: entry.dpX_Source_Out,
          Error_Duration: entry.error_Duration,
          MOV_Record_In: entry.moV_Record_In,
          MOV_Record_Out: entry.moV_Record_Out,
          Error_Code: entry.error_Code,
          Error_Remarks: entry.error_Remarks,
          Status: entry.actions,
          Remarks: entry.remarks,
          ProductionHours: 0,
        };

        await agent.NFAIRestoTimeSheet.NFAIProductivityStatusCreate(
          nfaiProductivitySend
        );
        
        await agent.NFAIRestoTimeSheet.NFAIMasteringProjectTimeSheetEntry(
          entry
        );
      }

      toast.success(`${markers.length} timesheet entries created successfully`);

      const { data: list } = await agent.NFAIRestoTimeSheet.getTimeSheetEntries(
        selectedProject || '',
        user?.departmentName || ''
      );
      setRows(list);

      resetForm();
    } catch (error) {
      console.log(error);
    } finally {
      setTableLoader(false);
    }
  };

  // ============================================
  // FIXED: File upload only enabled when CURRENT USER has active entry with QC scope
  // ============================================
  const fileUploadRender = () => {
    const hasActiveEntry = !!currentUserActiveEntry;
    const isScopeQC = formData.scope_of_Work.toLowerCase().includes('qc');
    const isEnabled = hasActiveEntry && isScopeQC;

    return (
      <div style={{ marginLeft: 'auto' }}>
        <div style={styles.container}>
          <input
            type="file"
            accept=".csv"
            onChange={handleChange}
            style={styles.input}
            disabled={!isEnabled}
          />
          {file && <p style={styles.fileName}>{file.name}</p>}
          <Button
            primary
            onClick={handleUpload}
            disabled={!isEnabled}
            style={styles.button}
          >
            Upload
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div
      style={{
        padding: '25px',
        transform: 'scale(0.557)',
        transformOrigin: 'top left',
      }}
    >
      <div
        style={{
          marginBottom: '20px',
          display: 'flex',
          gap: '20px',
          alignItems: 'center',
          width: '1000px',
        }}
      >
        <ProjectSelector
          options={projectOptions}
          value={selectedProject}
          loading={isUserAuthorized ? titleLoader : loader}
          onChange={(value: string) => {
            setSelectedProject(value as string);
          }}
        />
        {fileUploadRender()}
      </div>
      <div>
        {tableLoader ? (
          <Dimmer
            active={true}
            inverted
            style={{
              marginTop: '22px',
              width: '180%',
            }}
          >
            <Loader content={'Loading...'} />
          </Dimmer>
        ) : (
          <Table celled>
            <Table.Header>
              <Table.Row>
                {TimeSheetNewHeader.map((column) => (
                  <Table.HeaderCell key={column}>{column}</Table.HeaderCell>
                ))}
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {selectedProject &&
                rows.map((row) => (
                  <Table.Row key={row.id}>{renderTableRow(row)}</Table.Row>
                ))}
                
              {currentUserActiveEntry ? null : renderNewEntryRow()}
            </Table.Body>
          </Table>
        )}
      </div>
    </div>
  );
};

export default observer(NFAITimeSheetMastering);
